import { PartnerProductCreateDTO, PartnerProductUpdateDTO } from 'services/repositories/masterdata/masterdata';
import { NavigableItem } from 'services/schemableFormNavigation/schemableFormNavigation';

import { PRODUCT_ATTRIBUTE_NAMES, PRODUCT_IDENTIFIER_DOMAINS, ProductRow, PRODUCTS_DATA_PREFIX } from './productRows';

/**
 * One server-side validation error, and one stop for the previous/next controls. A cell-level item
 * is `products.<row>.<column>`; a row-level one is `products.<row>`, for an error the table has no
 * single cell for. `messages` is what the popover lists.
 *
 * A row with only cell errors has no item of its own: `ProductValidationService` highlights any
 * row that has an item in it, so that cycling through errors stops once per error, not once more
 * per highlighted row.
 */
export type ProductValidationItem = NavigableItem & {
  severity: 'error';
  scope: 'row' | 'cell';
  messages: Array<string>;
};

type PayloadProduct = PartnerProductUpdateDTO | PartnerProductCreateDTO;

/** One batch request, and the table row each of its products was built from. */
export type ValidationRequest = {
  products: Array<PayloadProduct>;
  rowIndexes: Array<number>;
};

export type MappedProductValidationErrors = {
  items: Array<ProductValidationItem>;
  /** Errors that name no product of this request (or none at all) — they can only go in the banner. */
  unmapped: Record<string, Array<string>>;
};

const PAYLOAD_KEY = /^products\.(\d+)(?:\.(.*))?$/;

/** A row's own path, `products.<row>` — as opposed to one of its cells. */
const ROW_PATH = new RegExp(`^${PRODUCTS_DATA_PREFIX}\\.\\d+$`);

/** Payload fields that are a column of their own in the table. */
const DIRECT_COLUMNS = ['unit_price', 'tax_rate', 'unit_of_measure', 'is_orderable', 'currency'];

/**
 * A 422 from the batch endpoints keys each error by position *in the request*
 * (`products.1.unit_price`), and that request leaves untouched rows out, so the position says
 * nothing about the table. `request.rowIndexes` is what translates it back; each error is then
 * pinned to a cell when the payload field it names is one the table shows, and to the row otherwise.
 *
 * The payload's `identifiers`/`attributes` don't line up with columns either — the table shows one
 * GTIN, one article number and the name, out of a longer list — so an entry counts as the GTIN/
 * article number/name cell only when it is the value that cell currently holds. Anything else (an
 * identifier the table never showed, an error on the collection as a whole) stays on the row.
 */
export const mapProductValidationErrors = (
  errors: Record<string, Array<string>>,
  request: ValidationRequest,
  rows: Array<ProductRow>,
): MappedProductValidationErrors => {
  const unmapped: Record<string, Array<string>> = {};
  const cellMessages = new Map<string, Array<string>>();
  const rowMessages = new Map<number, Array<string>>();

  Object.entries(errors).forEach(([key, messages]) => {
    const match = PAYLOAD_KEY.exec(key);
    const payloadIndex = match ? Number(match[1]) : -1;
    const rowIndex = request.rowIndexes[payloadIndex];
    const row = rows[rowIndex];
    if (!match || rowIndex === undefined || !row) {
      unmapped[key] = messages;
      return;
    }

    const column = findColumn(match[2], request.products[payloadIndex], row);
    if (column) {
      const fieldPath = cellPath(rowIndex, column);
      cellMessages.set(fieldPath, [...(cellMessages.get(fieldPath) ?? []), ...messages]);
    } else {
      rowMessages.set(rowIndex, [...(rowMessages.get(rowIndex) ?? []), ...messages]);
    }
  });

  const cellEntries = Array.from(cellMessages.entries());
  const affectedRows = Array.from(
    new Set([...Array.from(rowMessages.keys()), ...cellEntries.map(([fieldPath]) => rowIndexOf(fieldPath))]),
  ).sort((a, b) => a - b);

  const items = affectedRows.flatMap((rowIndex) => {
    const rowError = rowMessages.get(rowIndex);
    return [
      ...(rowError ? [toItem(rowPath(rowIndex), 'row', rowError)] : []),
      ...cellEntries
        .filter(([fieldPath]) => rowIndexOf(fieldPath) === rowIndex)
        .map(([fieldPath, messages]) => toItem(fieldPath, 'cell', messages)),
    ];
  });

  return { items, unmapped };
};

/**
 * What is left once the user has edited `fieldPath`. The cell's own error goes — it describes the
 * value that was sent, not the new one — and so does any error that belongs to the row as a whole:
 * it has no cell to correct, so touching the row is the only way the user can say they have dealt
 * with it, and leaving it would keep the save blocked with nothing to fix. (If the server still
 * objects, the next save says so again.)
 */
export const withoutEditedField = (
  items: Array<ProductValidationItem>,
  fieldPath: string,
): Array<ProductValidationItem> => {
  const rowIndex = rowIndexOf(fieldPath);
  const remaining = items.filter(
    (item) => item.fieldPath !== fieldPath && !(item.scope === 'row' && rowIndexOf(item.fieldPath) === rowIndex),
  );

  return remaining.length === items.length ? items : remaining;
};

/** A product marked for deletion is going away, so what the server had against it no longer matters. */
export const withoutRowErrors = (
  items: Array<ProductValidationItem>,
  rowIndex: number,
): Array<ProductValidationItem> => {
  const remaining = items.filter((item) => rowIndexOf(item.fieldPath) !== rowIndex);
  return remaining.length === items.length ? items : remaining;
};

export const isRowPath = (fieldPath: string) => ROW_PATH.test(fieldPath);

export const rowPath = (rowIndex: number) => `${PRODUCTS_DATA_PREFIX}.${rowIndex}`;

export const rowIndexOf = (fieldPath: string) => Number(fieldPath.split('.')[1]);

const cellPath = (rowIndex: number, column: string) => `${rowPath(rowIndex)}.${column}`;

const toItem = (
  fieldPath: string,
  scope: ProductValidationItem['scope'],
  messages: Array<string>,
): ProductValidationItem => ({
  id: `validation:${fieldPath}`,
  fieldPath,
  severity: 'error',
  type: 'server',
  scope,
  messages,
});

const findColumn = (remainder: string | undefined, product: PayloadProduct, row: ProductRow): string | undefined => {
  if (!remainder) {
    return undefined;
  }

  const [field, position] = remainder.split('.');
  if (DIRECT_COLUMNS.includes(field)) {
    return field;
  }

  const entryIndex = Number(position);
  if (!Number.isInteger(entryIndex)) {
    return undefined;
  }

  if (field === 'identifiers') {
    const identifier = product.identifiers?.[entryIndex];
    if (identifier?.domain === PRODUCT_IDENTIFIER_DOMAINS.gtin && identifier.value === row.gtin) {
      return 'gtin';
    }
    if (identifier?.domain === PRODUCT_IDENTIFIER_DOMAINS.articleNumber && identifier.value === row.articleNumber) {
      return 'articleNumber';
    }
  }

  if (field === 'attributes') {
    const attribute = product.attributes?.[entryIndex];
    if (attribute?.name === PRODUCT_ATTRIBUTE_NAMES.name && attribute.value === row.name) {
      return 'name';
    }
  }

  return undefined;
};
